export const ALGOS = ["MD5", "SHA-1", "SHA-256", "SHA-384", "SHA-512"] as const;
export type Algo = (typeof ALGOS)[number];

type Hasher = { init(): unknown; update(d: Uint8Array): unknown; digest(): string };

async function makeHasher(algo: Algo): Promise<Hasher> {
  const w = await import("hash-wasm");
  switch (algo) {
    case "MD5": return (await w.createMD5()) as Hasher;
    case "SHA-1": return (await w.createSHA1()) as Hasher;
    case "SHA-256": return (await w.createSHA256()) as Hasher;
    case "SHA-384": return (await w.createSHA384()) as Hasher;
    case "SHA-512": return (await w.createSHA512()) as Hasher;
  }
}

/** Hashes bytes (or a File, read in chunks so big files don't fill memory). */
export async function hashAll(
  source: Uint8Array | Blob,
  onProgress?: (fraction: number) => void
): Promise<Record<Algo, string>> {
  const hashers = await Promise.all(ALGOS.map(async a => [a, await makeHasher(a)] as const));
  hashers.forEach(([, h]) => h.init());
  const CHUNK = 4 * 1024 * 1024;
  if (source instanceof Uint8Array) {
    hashers.forEach(([, h]) => h.update(source));
  } else {
    for (let off = 0; off < source.size; off += CHUNK) {
      const buf = new Uint8Array(await source.slice(off, off + CHUNK).arrayBuffer());
      hashers.forEach(([, h]) => h.update(buf));
      onProgress?.(Math.min(1, (off + CHUNK) / source.size));
    }
  }
  const out = {} as Record<Algo, string>;
  for (const [a, h] of hashers) out[a] = h.digest();
  return out;
}

export function matchesExpected(hashes: Record<string, string>, expected: string): string | null {
  const e = expected.trim().toLowerCase().replace(/\s+/g, "");
  if (!e) return null;
  return Object.entries(hashes).find(([, v]) => v === e)?.[0] ?? "";
}
